// src/admin/api/permissionsApi.js
//
// Permission catalog API for the admin pages. Same module shape as
// connectors/api/connectorsApi.js and accounts/api/accountsApi.js — plain
// async functions returning UI-shaped objects — except those two are mocks
// and this talks to the real backend, so every call also normalizes the
// domain-exception envelope through toAdminError.
//
// Mirrors presentation/shared/access_provisioning/rbac_controller.py:
//   GET    /api/auth/permissions       → {items, total, page, page_size, total_pages}
//   POST   /api/auth/permissions       → {name, description}
//   PUT    /api/auth/permissions/{id}  → {name?, description?, is_active?}  (partial)
//   DELETE /api/auth/permissions/{id}  → soft-deactivate, returns the record
//
// api/authApi.js keeps the older server-paginated listPermissions() that the
// role-builder's checkbox picker still calls. This module is the whole-list +
// writes surface the admin page needs; the two don't overlap.

import { api } from "../../api/client";
import { toAdminError } from "./adminApiError";
import { fetchAllPages } from "./fetchAllPages";

// snake_case → camelCase happens here and nowhere else, so no component ever
// reads `is_active`. `module` is pre-split off the "<module>:<action>" name
// so a cell renderer doesn't re-parse it on every render.
function toPermission(dto) {
  return {
    id: dto.id,
    name: dto.name,
    description: dto.description || "",
    isActive: Boolean(dto.is_active),
    module: String(dto.name || "").split(":")[0] || "—",
  };
}

/**
 * Every permission, across all pages.
 *
 * See fetchAllPages for why this is assembled page by page.
 */
export async function listPermissions() {
  try {
    return (await fetchAllPages("/auth/permissions")).map(toPermission);
  } catch (err) {
    throw toAdminError(err, "Failed to load permissions.");
  }
}

export async function createPermission({ name, description }) {
  try {
    const res = await api.post("/auth/permissions", {
      name: name.trim(),
      description: description?.trim() || "",
    });
    return toPermission(res.data);
  } catch (err) {
    throw toAdminError(err, "Could not create the permission.", "name");
  }
}

// PUT is partial (UpdatePermissionRequest — every field Optional), but the
// form always submits both editable fields, so both are always sent.
export async function updatePermission(id, { name, description }) {
  try {
    const res = await api.put(`/auth/permissions/${id}`, {
      name: name.trim(),
      description: description?.trim() || "",
    });
    return toPermission(res.data);
  } catch (err) {
    throw toAdminError(err, "Could not update the permission.", "name");
  }
}

/** Soft-deactivate (is_active=false) — the row stays and roles keep referencing it. */
export async function deactivatePermission(id) {
  try {
    const res = await api.delete(`/auth/permissions/${id}`);
    return toPermission(res.data);
  } catch (err) {
    throw toAdminError(err, "Could not deactivate the permission.");
  }
}
